import { type Product } from '@/mocks/products';

interface ProductAccordionProps {
  product: Product;
}

interface AccordionSectionProps {
  title: string;
  icon: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

function AccordionSection({ title, icon, defaultOpen = false, children }: AccordionSectionProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-background-200/70 last:border-b-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-4 text-left cursor-pointer group"
      >
        <span className="flex items-center gap-3">
          <span className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100 text-accent-500 flex-shrink-0">
            <i className={icon}></i>
          </span>
          <span className="font-heading text-sm font-bold text-foreground-800 group-hover:text-foreground-900 transition-colors duration-150">
            {title}
          </span>
        </span>
        <span className="w-6 h-6 flex items-center justify-center flex-shrink-0">
          {open ? (
            <i className="ri-subtract-line text-foreground-400"></i>
          ) : (
            <i className="ri-add-line text-foreground-400"></i>
          )}
        </span>
      </button>
      {open && (
        <div className="pb-4 pl-11 text-sm text-foreground-600 leading-relaxed">
          {children}
        </div>
      )}
    </div>
  );
}

export default function ProductAccordion({ product }: ProductAccordionProps) {
  return (
    <div className="bg-secondary-100/60 rounded-2xl overflow-hidden">
      {/* Materials & Safety */}
      <AccordionSection title="Materials & Safety" icon="ri-shield-check-line" defaultOpen={true}>
        <ul className="space-y-2">
          {product.materialClaims.map((claim, idx) => (
            <li key={idx} className="flex items-start gap-2">
              <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-footprint-line text-accent-500 text-xs"></i>
              </span>
              <span>{claim}</span>
            </li>
          ))}
          {product.petSubcategory && (
            <li className="flex items-start gap-2">
              <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-footprint-line text-accent-500 text-xs"></i>
              </span>
              <span>Recommended for: {product.petSubcategory}</span>
            </li>
          )}
        </ul>
      </AccordionSection>

      {/* Care Instructions */}
      <AccordionSection title="Care Instructions" icon="ri-heart-line">
        <p>{product.careInstructions}</p>
      </AccordionSection>

      {/* Shipping & Delivery */}
      <AccordionSection title="Shipping & Delivery" icon="ri-truck-line">
        <p>{product.shippingInfo}</p>
      </AccordionSection>

      {/* FAQs */}
      <AccordionSection title="FAQs" icon="ri-question-answer-line">
        <div className="space-y-4">
          {product.faqs.map((faq, idx) => (
            <div key={idx}>
              <p className="font-semibold text-foreground-800 mb-1">{faq.question}</p>
              <p className="text-foreground-500">{faq.answer}</p>
            </div>
          ))}
        </div>
      </AccordionSection>
    </div>
  );
}